'use client'
import {
   AiMlAutomation,
   BusinessGrow,
   ContactUsForm,
   FAQs,
   GameChangingIdea,
   TechNServicesHeroSection,
   TemplateSlider,
   WordsOfAppreciation,
} from '@/components'
import { ReactNativeIcon } from '@/svgs'
import React from 'react'
import CommonTechGrid from '../../Components/CommonTechGrid'
import ReactNativeFromStrategy from './components/ReactNativeFromStrategy'
import ReactNativeWhyChoose from './components/ReactNativeWhyChoose'

const slides = [
   {
      icon: '/assets/svg/technologies/reactNative/cross.svg',
      topText: 'Cross-Platform Mobile Apps',
      header: 'Fast & Scalable Apps',
      details:
         'Develop high-performance mobile applications for iOS and Android using a single React Native codebase.',
   },
   {
      icon: '/assets/svg/technologies/reactNative/smooth.svg',
      topText: 'UI/UX & Animations',
      header: 'Smooth & Engaging UI',
      details:
         "Create stunning interfaces with React Native's Flexbox, Gesture Handler, and animated interactions for better user experiences.",
   },
   {
      icon: '/assets/svg/technologies/reactNative/api.svg',
      topText: 'API & Backend Integration',
      header: 'Seamless API Connectivity',
      details:
         'Connect React Native apps with RESTful APIs, GraphQL, Firebase, and cloud services for real-time data management.',
   },
   {
      icon: '/assets/svg/technologies/reactNative/security.svg',
      topText: 'Security & Performance Optimization',
      header: 'Optimized & Secure Apps',
      details:
         'Enhance app security with authentication, encryption, and performance optimization using lazy loading and code splitting.',
   },
   {
      icon: '/assets/svg/technologies/reactNative/native.svg',
      topText: 'Native Module Integrations',
      header: 'Advanced Native Features',
      details:
         'Integrate native modules for camera, GPS, push notifications, and offline storage using Swift, Kotlin, and third-party libraries',
   },
   {
      icon: '/assets/svg/technologies/reactNative/deployment.svg',
      topText: 'App Deployment & Scaling',
      header: 'Publish & Scale Apps',
      details:
         'Deploy React Native applications on App Store and Google Play with CI/CD, OTA updates and app store optimization (ASO)',
   },
]

const gridContent = [
   {
      title: 'Health & Fitness',
      subtitle: 'React Native For Healthcare',
      description:
         'Develop secure healthcare apps with React Native. Build HIPAA-compliant telemedicine platforms, AI-driven patient monitoring, and real-time diagnostics for seamless cross-platform medical services.',
      url: '/assets/svg/technologies/angular/Health.svg',
   },
   {
      title: 'Banking & Finance',
      subtitle: 'React Native For Finance & Banking',
      description:
         'Enhance mobile banking with React Native. Develop AI-powered fraud detection, secure authentication, and real-time financial dashboards for a smooth and secure banking experience.',
      url: '/assets/svg/technologies/angular/Fintech.svg',
   },
   {
      title: 'Restaurants & Hospitality',
      subtitle: 'React Native For Restaurants & Hospitality',
      description:
         'Optimize restaurant management with React Native. Implement AI-based menu suggestions, auto-reservations, and live order tracking for an effortless customer experience.',
      url: '/assets/svg/technologies/angular/Food.svg',
   },
   {
      title: 'Enhanced eCommerce',
      subtitle: 'React Native for Enhanced eCommerce',
      description:
         'Boost eCommerce with React Native-powered apps. Enable AI-driven product recommendations, frictionless checkout experiences, and real-time inventory management for increased sales conversions.',
      url: '/assets/svg/technologies/angular/Ecommerce.svg',
   },
   {
      title: 'Logistics & Supply Chain',
      subtitle: 'React Native For Logistics & Supply Chain',
      description:
         'Streamline logistics with React Native. Provide real-time shipment tracking, route optimization with AI, and automated fleet management for effective supply chain management.',
      url: '/assets/svg/technologies/angular/Supply.svg',
   },
   {
      title: 'Social Networking',
      subtitle: 'React Native For Social Networking',
      description:
         'Build feature-rich social networking apps with React Native. Implement real-time messaging, AI-powered content suggestions, and dynamic UI/UX for enhanced user engagement.',
      url: '/assets/svg/technologies/angular/Social.svg',
   },
   {
      title: 'Gaming & Sports Analytics',
      subtitle: 'React Native For Gaming & Sports Analytics',
      description:
         'Augment gaming and sports analytics with React Native. Create real-time leaderboards, AI-based player insights, and interactive dashboards for engaging sports and gaming experiences.',
      url: '/assets/svg/technologies/angular/Gaming.svg',
   },
   {
      title: 'Travel & Tourism',
      subtitle: 'React Native For Travel & Tourism',
      description:
         'Improve travel booking experiences with React Native-powered apps. Implement AI-driven itinerary planners, real-time booking, and chatbot-driven customer support for smooth travel services.',
      url: '/assets/svg/technologies/angular/travel.svg',
   },
   {
      title: 'Real Estate',
      subtitle: 'React Native For Real Estate',
      description:
         'React Native enhances real estate apps with AI-driven property searches, virtual 3D tours, and real-time market analytics, simplifying property transactions.',
      url: '/assets/svg/technologies/angular/Real-Estate.svg',
   },
   {
      title: 'Education & E-Learning',
      subtitle: 'React Native For Education & E-Learning',
      description:
         'Transform digital learning with React Native-powered LMS apps. Enable AI-powered adaptive learning, interactive video courses, and automated assessments for a more engaging experience.',
      url: '/assets/svg/technologies/angular/Edu.svg',
   },
   {
      title: 'Media & Entertainment',
      subtitle: 'React Native For Media & Entertainment',
      description:
         'Enhance media streaming with React Native apps. Implement AI-driven content recommendations, real-time engagement features, and intuitive UI for personalized digital entertainment.',
      url: '/assets/svg/technologies/angular/Media.svg',
   },
   {
      title: 'Government & Public Sector',
      subtitle: 'React Native For Government & Public Sector',
      description:
         'Modernize public services with React Native-powered mobile apps. Develop secure authentication, AI-driven citizen engagement tools, and real-time government service portals for efficiency and accessibility.',
      url: '/assets/svg/technologies/angular/Gov.svg',
   },
]

const faqData = [
   {
      question:
         'What does React Native do and how does it assist developers in making apps for multiple platforms?',
      answer:
         'Built by Meta as an open-source project, React Native uses just one JavaScript language for developing apps for iOS and Android. It gives you almost the same results as a native app but cuts the time and expenses needed for development.',
   },
   {
      question:
         'How does React Native benefit businesses building mobile applications?',
      answer:
         'Using React Native, software teams can work faster, use the same code for every device and save time on maintenance. Apps can be brought to market faster by businesses while still giving everyone a similar experience on both iOS and Android.',
   },
   {
      question:
         'Which app categories are most appropriate for React Native development?',
      answer:
         'Developers should use React Native for social media, shopping, healthcare, fintech, MVP and any app that benefits from fast development, frequent changes and a memorable UI.',
   },
   {
      question:
         'What are the key advantages of using React Native over native development?',
      answer:
         'React Native supports code reuse, quicker testing, many useful plugins and the chance to use native features through modules that adjust to each system—all at a lower cost of development.',
   },
   {
      question:
         'Is React Native appropriate for large-scale or enterprise-level apps?',
      answer:
         'Firms like Facebook, Walmart and Coinbase have relied on React Native to build high-performance applications. When correctly set up, it grows organically for large and complex business needs.',
   },
   {
      question: 'How secure is React Native for mobile app development?',
      answer:
         'React Native makes it possible to use encryption, save data safely, rely on biometric authentication and use HTTPS communication. Security in mobile apps can be reinforced by using both third-party tools and official native modules.',
   },
   {
      question: 'How much time is needed to develop a React Native app?',
      answer:
         'A simple cross-platform application can usually be made in 3–5 weeks, but more elaborate apps that include non-standard UIs, APIs and native features might require 2–5 months to produce.',
   },
   {
      question: 'What tools and libraries are commonly used with React Native?',
      answer:
         'You can use Expo, Redux, React Navigation, Firebase, Axios, Native Base and Jest and Detox as popular tools for React Native. They support efficient development and make an application perform better and handle more traffic.',
   },
   {
      question: 'Can we use cameras and GPS in React Native apps?',
      answer:
         'Through the use of modules and packages, React Native permits access to camera, GPS, push notifications, biometrics and other important device features by developers.',
   },
   {
      question:
         'What should I look for in a React Native development team or agency?',
      answer:
         'Select a company that knows React, React Native, works on multi-platform apps, tweaks performance and integrations with tools and understands the rules of UX on mobile devices.',
   },
]
function ReactNativePage() {
   return (
      <>
         <TechNServicesHeroSection
            title={`React Native Development company`}
            subtitle="React Native Development Services for Scalable Mobile Apps"
            description="Our React Native development services build high-performance, cross-platform mobile applications with a native-like experience. Using reusable components, fast rendering, and seamless integrations, we optimize app performance, enhance user engagement, and ensure scalable solutions for businesses in a competitive digital landscape."
            tags={[
               'Custom React Native Mobile App Development',
               'High-Performance and Native-Like Mobile Apps',
               'Scalable and Cross-Platform React Native Solutions',
               'React Native Development',
               'Mobile App Solutions',
            ]}
            HeroIcon={ReactNativeIcon}
         />
         <BusinessGrow />
         <AiMlAutomation
            amdesc="We leverage our expertise in building high-performance, cross-platform React Native applications for iOS and Android. Our custom React Native solutions ensure seamless UI/UX, optimized performance, and cost-effective development for evolving business needs."
            amsubtitle={'Expert React Native Development Solutions'}
            amtitle={'Scalable & Secure React Native Development Services'}
            slides={slides}
         />

         <CommonTechGrid
            gridTitle={'React Native-Powered Solutions:'}
            gridSubTitle={
               'Quick & Scalable Cross-Platform Apps for Any Business'
            }
            gridTechbody={
               'Building Cross-Platform, Fast, and Engaging Mobile Applications'
            }
            gridDesc={
               'We at Benzatine, are experts in React Native development, designing high-performance mobile applications for iOS and Android. Our secure, fast, and scalable solutions drive user engagement, improve performance, and speed time-to-market, allowing businesses to prosper with contemporary UI/UX, seamless integrations, and cost-saving cross-platform development.'
            }
            gridContent={gridContent}
         />

         <ReactNativeFromStrategy />
         <ReactNativeWhyChoose />
         <GameChangingIdea />

         <section className="template-slider-section page_top_section section_secondary_bg">
            <div className="container">
               <h4 className="section_heading_title tech_title our-successfull-section">
                  Our Successful Web Application Development Stories
               </h4>
               <p className="features_description m-auto mb-4">
                  Innovative solutions that transformed businesses and delivered
                  real results.
               </p>
            </div>
            <div>
               <TemplateSlider />
            </div>
         </section>

         <section className="section appreciation-slider-section page_top_section section_secondary_bg pt-0">
            <WordsOfAppreciation />
         </section>

         <FAQs faqsConst={faqData} />

         <ContactUsForm />
      </>
   )
}

export default ReactNativePage
